import { useState, useEffect, useCallback } from 'react';

const API = 'https://tcio-api.onrender.com';

const s = {
  wrap: { padding: 32, maxWidth: 1100, margin: '0 auto' },
  h1: { margin: '0 0 4px', fontSize: 24, color: '#336090' },
  sub: { margin: '0 0 20px', color: '#666', fontSize: 14 },
  card: { background: 'white', borderRadius: 12, padding: 16, marginBottom: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  pill: { display: 'inline-block', padding: '2px 10px', borderRadius: 10, fontSize: 11, fontWeight: 800 },
  btn: { padding: '6px 14px', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: '1px solid #d1d5db', background: 'white', marginRight: 6 },
  input: { padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13 },
};

const SEV = { red: { bg: '#fee2e2', fg: '#991b1b' }, orange: { bg: '#ffedd5', fg: '#9a3412' } };
const FLAG_LABEL = {
  referral_missing_lead: '🔴 Referral never entered — possible diversion',
  prior_contact_on_first_call: '🟠 "First" call references earlier contact',
};

export default function Compliance({ user }) {
  const [view, setView] = useState('flags');
  const [flags, setFlags] = useState([]);
  const [refs, setRefs] = useState([]);
  const [noteFor, setNoteFor] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const headers = { Authorization: 'Bearer ' + user.token };
  const jh = { ...headers, 'Content-Type': 'application/json' };

  const load = useCallback(async () => {
    const [f, r] = await Promise.all([
      fetch(API + '/api/compliance-flags?status=open', { headers }).then(x => x.json()),
      fetch(API + '/api/expected-referrals', { headers }).then(x => x.json()),
    ]);
    if (f.ok) setFlags(f.data);
    if (r.ok) setRefs(r.data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { load(); }, [load]);

  const resolve = async (id, disposition) => {
    setBusy(true);
    await fetch(API + '/api/compliance-flags/' + id, {
      method: 'PATCH', headers: jh,
      body: JSON.stringify({ disposition, disposition_note: note || null }),
    });
    setBusy(false); setNoteFor(null); setNote(''); load();
  };

  const sweep = async () => {
    setBusy(true);
    await fetch(API + '/api/compliance/sweep', { method: 'POST', headers });
    setTimeout(() => { setBusy(false); load(); }, 3000);
  };

  const tabBtn = (on) => ({ padding: '8px 16px', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer', border: '1px solid #cbd5e1', background: on ? '#336090' : 'white', color: on ? 'white' : '#374151', marginRight: 8 });

  return (
    <div style={s.wrap}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div>
          <h1 style={s.h1}>Compliance</h1>
          <p style={s.sub}>Referral reconciliation and off-platform contact detection. Flags are evidence for your judgment — nothing here is an accusation.</p>
        </div>
        <button style={s.btn} onClick={sweep} disabled={busy}>{busy ? 'Working…' : '↻ Run sweep now'}</button>
      </div>

      <div style={{ marginBottom: 16 }}>
        <button style={tabBtn(view === 'flags')} onClick={() => setView('flags')}>⚠️ Open Flags ({flags.length})</button>
        <button style={tabBtn(view === 'refs')} onClick={() => setView('refs')}>📋 Referral Ledger ({refs.length})</button>
      </div>

      {view === 'flags' && (flags.length === 0
        ? <div style={{ ...s.card, textAlign: 'center', color: '#16a34a', fontWeight: 700 }}>✓ No open flags — everything reconciles.</div>
        : flags.map(f => (
          <div key={f.id} style={{ ...s.card, borderLeft: '4px solid ' + (SEV[f.severity] ? SEV[f.severity].fg : '#64748b') }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
              <span style={{ ...s.pill, background: SEV[f.severity] && SEV[f.severity].bg, color: SEV[f.severity] && SEV[f.severity].fg }}>
                {FLAG_LABEL[f.flag_type] || f.flag_type}
              </span>
              <span style={{ fontSize: 12, color: '#94a3b8' }}>{new Date(f.created_at).toLocaleString('en-US')}</span>
            </div>
            {f.lead_name && <div style={{ fontSize: 14, fontWeight: 700, marginTop: 6 }}>{f.lead_name}{f.rep_first ? ' · rep: ' + f.rep_first + ' ' + f.rep_last : ''}</div>}
            <div style={{ fontSize: 13, color: '#334155', marginTop: 6, whiteSpace: 'pre-wrap' }}>{f.evidence}</div>
            <div style={{ marginTop: 10 }}>
              {noteFor === f.id ? (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  <input style={{ ...s.input, width: 320 }} placeholder="Note (optional)" value={note} onChange={e => setNote(e.target.value)} />
                  <button style={{ ...s.btn, background: '#f0fdf4', borderColor: '#bbf7d0', color: '#166534' }} disabled={busy} onClick={() => resolve(f.id, 'legitimate')}>✓ Legitimate</button>
                  <button style={{ ...s.btn, background: '#fefce8', borderColor: '#fde68a', color: '#854d0e' }} disabled={busy} onClick={() => resolve(f.id, 'discussed_with_rep')}>Discussed with rep</button>
                  <button style={{ ...s.btn, background: '#fef2f2', borderColor: '#fca5a5', color: '#991b1b' }} disabled={busy} onClick={() => resolve(f.id, 'escalated')}>Escalate</button>
                  <button style={s.btn} onClick={() => { setNoteFor(null); setNote(''); }}>Cancel</button>
                </div>
              ) : (
                <button style={s.btn} onClick={() => setNoteFor(f.id)}>Review…</button>
              )}
            </div>
          </div>
        )))}

      {view === 'refs' && (
        <div style={s.card}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr>{['Referred', 'Partner', 'Client', 'Phone', 'Status', 'Matched Lead'].map(h =>
              <th key={h} style={{ textAlign: 'left', padding: '8px 10px', fontSize: 11, color: '#666', textTransform: 'uppercase', borderBottom: '2px solid #e5e7eb' }}>{h}</th>)}</tr></thead>
            <tbody>
              {refs.map(r => (
                <tr key={r.id}>
                  <td style={{ padding: '8px 10px', borderBottom: '1px solid #f0f4f8' }}>{(() => { const s = String(r.referred_at).slice(0,10); const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/); return (m ? new Date(+m[1], +m[2]-1, +m[3]) : new Date(r.referred_at)).toLocaleDateString('en-US'); })()}</td>
                  <td style={{ padding: '8px 10px', borderBottom: '1px solid #f0f4f8' }}>{r.partner_name}</td>
                  <td style={{ padding: '8px 10px', borderBottom: '1px solid #f0f4f8', fontWeight: 600 }}>{r.client_name || '—'}</td>
                  <td style={{ padding: '8px 10px', borderBottom: '1px solid #f0f4f8' }}>{r.client_phone || '—'}</td>
                  <td style={{ padding: '8px 10px', borderBottom: '1px solid #f0f4f8' }}>
                    <span style={{ ...s.pill, background: r.status === 'matched' ? '#dcfce7' : r.status === 'flagged' ? '#fee2e2' : '#fef9c3', color: r.status === 'matched' ? '#166534' : r.status === 'flagged' ? '#991b1b' : '#854d0e' }}>
                      {r.status === 'matched' ? '✓ IN TCIO' : r.status === 'flagged' ? '🔴 MISSING' : '⏳ AWAITING'}
                    </span>
                  </td>
                  <td style={{ padding: '8px 10px', borderBottom: '1px solid #f0f4f8' }}>{r.matched_lead_name || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
